import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { useManualProvisionEdge } from '../hooks/use-manual-provision-edge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { Spinner } from '@/components/ui/spinner'

interface ManualProvisionModalProps {
  open: boolean
  onClose: () => void
}

export function ManualProvisionModal({
  open,
  onClose,
}: ManualProvisionModalProps) {
  const { t: tEdges } = useTranslation('edges')
  const { data: restaurants } = useRestaurants()
  const [restaurantId, setRestaurantId] = useState<string>('')
  const [name, setName] = useState('')
  const [edgeId, setEdgeId] = useState<number | null>(null)
  const [edgeKey, setEdgeKey] = useState('')
  const [showKey, setShowKey] = useState(false)
  const mutation = useManualProvisionEdge()

  useEffect(() => {
    if (!open) {
      setRestaurantId('')
      setName('')
      setEdgeId(null)
      setEdgeKey('')
      setShowKey(false)
    }
  }, [open])

  const handleSubmit = () => {
    if (!restaurantId) {
      toast.error(tEdges('manualModal.missingRestaurant'))
      return
    }
    mutation.mutate(
      { restaurantId: parseInt(restaurantId), name: name.trim() || undefined },
      {
        onSuccess: (res) => {
          setEdgeId(res.edgeId)
          setEdgeKey(res.edgeKey)
          toast.success(tEdges('manualModal.provisioned'))
        },
      },
    )
  }

  const handleCopy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value)
      toast.success(tEdges('manualModal.copySuccess', { label }))
    } catch {
      toast.error(tEdges('manualModal.copyFailed'))
    }
  }

  const handleClose = () => onClose()
  const showResult = edgeId !== null && edgeKey

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{tEdges('manualModal.title')}</DialogTitle>
          <DialogDescription>
            {tEdges('manualModal.description')}
          </DialogDescription>
        </DialogHeader>

        {!showResult ? (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>{tEdges('manualModal.restaurantLabel')}</Label>
              <Select value={restaurantId} onValueChange={setRestaurantId}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={tEdges('manualModal.restaurantPlaceholder')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {restaurants?.items.map((r) => (
                    <SelectItem key={r.id} value={r.id.toString()}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{tEdges('manualModal.edgeNameLabel')}</Label>
              <Input
                placeholder={tEdges('manualModal.edgeNamePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                {tEdges('manualModal.edgeIdLabel')}
              </p>
              <div className="flex items-center gap-2">
                <code className="px-2 py-1 rounded bg-muted">{edgeId}</code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleCopy(
                      String(edgeId),
                      tEdges('manualModal.edgeIdShort'),
                    )
                  }
                >
                  {tEdges('manualModal.copy')}
                </Button>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                {tEdges('manualModal.edgeKeyLabel')}
              </p>
              <div className="flex items-center gap-2">
                <code className="px-2 py-1 rounded bg-muted">
                  {showKey ? edgeKey : '••••••••••••••••'}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowKey((v) => !v)}
                >
                  {showKey
                    ? tEdges('manualModal.hide')
                    : tEdges('manualModal.show')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleCopy(edgeKey, tEdges('manualModal.edgeKeyShort'))
                  }
                >
                  {tEdges('manualModal.copy')}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {tEdges('manualModal.configureWith')}
                <br />
                <code>
                  {tEdges('manualModal.edgeIdConfig')} {edgeId}
                </code>
                <br />
                <code>
                  {tEdges('manualModal.edgeKeyConfig')} {edgeKey}
                </code>
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {!showResult ? (
            <>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={mutation.isPending}
              >
                {tEdges('manualModal.cancel')}
              </Button>
              <Button onClick={handleSubmit} disabled={mutation.isPending}>
                {mutation.isPending && <Spinner className="w-4 h-4 mr-2" />}
                {tEdges('manualModal.provision')}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>{tEdges('manualModal.done')}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
